import { type ReactNode, useEffect, useId, useRef } from 'react'

import { cn } from '../cn'

// Checkbox + RadioGroup (design/components/choice.html). Native controls inside
// a <label> so clicking the text toggles and keyboard works for free. Checkbox
// = independent options (notifications, terms); RadioGroup = exactly one (tone,
// model). Selection is shown by check/dot AND colour, never colour alone.

export interface CheckboxProps {
  checked: boolean | 'indeterminate'
  onChange: (checked: boolean) => void
  label: ReactNode
  description?: string
  disabled?: boolean
  className?: string
}

/** Native checkbox wrapped in a <label> so clicking the text toggles and
 *  keyboard works for free; `checked: 'indeterminate'` renders the mixed
 *  select-all state (a click from it reports true). */
export function Checkbox({
  checked,
  onChange,
  label,
  description,
  disabled,
  className,
}: CheckboxProps) {
  const ref = useRef<HTMLInputElement>(null)
  const indeterminate = checked === 'indeterminate'

  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate
  }, [indeterminate])

  return (
    <label
      className={cn(
        'inline-flex items-start gap-2 text-[12px] text-text',
        disabled ? 'cursor-not-allowed text-text-3' : 'cursor-pointer',
        className,
      )}
      aria-disabled={disabled ? true : undefined}
    >
      <span className="relative inline-flex flex-none">
        <input
          ref={ref}
          type="checkbox"
          checked={indeterminate ? false : checked}
          disabled={disabled}
          onChange={(e) => !disabled && onChange(e.target.checked)}
          className="peer size-4 appearance-none rounded-[4px] border-[1.5px] border-input-line bg-surface transition-colors checked:border-brand-blue checked:bg-brand-blue indeterminate:border-brand-blue indeterminate:bg-brand-blue focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-blue disabled:cursor-not-allowed disabled:border-line disabled:bg-field disabled:checked:border-text-3 disabled:checked:bg-text-3 disabled:indeterminate:border-text-3 disabled:indeterminate:bg-text-3"
        />
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
          className="pointer-events-none absolute left-1/2 top-1/2 hidden size-[10px] -translate-x-1/2 -translate-y-1/2 text-white peer-checked:block"
        >
          <path d="M20 6 9 17l-5-5" />
        </svg>
        <span
          aria-hidden
          className="pointer-events-none absolute left-1/2 top-1/2 hidden h-0.5 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white peer-indeterminate:block"
        />
      </span>
      {description ? (
        <span className="flex flex-col gap-px">
          <span>{label}</span>
          <span className="text-[10.5px] text-text-3">{description}</span>
        </span>
      ) : (
        <span>{label}</span>
      )}
    </label>
  )
}

export interface RadioOption<T extends string> {
  value: T
  label: string
  description?: string
  disabled?: boolean
}

export interface RadioGroupProps<T extends string> {
  label: string
  name: string
  value: T
  options: RadioOption<T>[]
  onChange: (v: T) => void
  /** Hide the visible group label (still used as the group's aria-label). */
  hideLabel?: boolean
  className?: string
}

/** Exactly-one choice from native radios sharing `name` — Checkbox covers
 *  independent options. `hideLabel` keeps the group label as aria-label
 *  only. */
export function RadioGroup<T extends string>({
  label,
  name,
  value,
  options,
  onChange,
  hideLabel,
  className,
}: RadioGroupProps<T>) {
  const labelId = useId()
  return (
    <div
      role="radiogroup"
      aria-labelledby={hideLabel ? undefined : labelId}
      aria-label={hideLabel ? label : undefined}
      className={cn('flex w-full flex-col items-start gap-[11px]', className)}
    >
      {!hideLabel && (
        <span id={labelId} className="mb-0.5 text-[11.5px] font-semibold text-text">
          {label}
        </span>
      )}
      {options.map((o) => (
        <label
          key={o.value}
          className={cn(
            'inline-flex items-start gap-2 text-[12px] text-text',
            o.disabled ? 'cursor-not-allowed text-text-3' : 'cursor-pointer',
          )}
          aria-disabled={o.disabled ? true : undefined}
        >
          <span className="relative inline-flex flex-none">
            <input
              type="radio"
              name={name}
              value={o.value}
              checked={value === o.value}
              disabled={o.disabled}
              onChange={() => onChange(o.value)}
              className="peer size-4 appearance-none rounded-full border-[1.5px] border-input-line bg-surface transition-colors checked:border-brand-blue focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-blue disabled:cursor-not-allowed disabled:border-line disabled:bg-field"
            />
            <span
              aria-hidden
              className="pointer-events-none absolute inset-[3px] hidden rounded-full bg-brand-blue peer-checked:block peer-disabled:bg-text-3"
            />
          </span>
          {o.description ? (
            <span className="flex flex-col gap-px">
              <span>{o.label}</span>
              <span className="text-[10.5px] text-text-3">{o.description}</span>
            </span>
          ) : (
            <span>{o.label}</span>
          )}
        </label>
      ))}
    </div>
  )
}
